import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { useApp } from '../../../app/AppContext.jsx'

import { Close } from '../icons/index.js'

import styles from './Toast.module.scss'

/**
 * Toast — portal-rendered error/info notification with auto-dismiss.
 *
 * Renders inside the current app instance portal host.
 * Auto-dismisses after `duration` ms; also dismissable via close button.
 *
 * @param {object}      props
 * @param {string}      props.message      - Text to display
 * @param {() => void}  props.onClose      - Called on dismiss (auto or manual)
 * @param {number}      [props.duration]   - Auto-dismiss delay in ms (default 5000)
 * @param {'error'|'info'} [props.variant] - Visual variant (default 'error')
 */
export function Toast({ message, onClose, duration = 5000, variant = 'error' }) {
  const { portalHostId } = useApp()

  useEffect(() => {
    const timer = setTimeout(onClose, duration)
    return () => clearTimeout(timer)
  }, [onClose, duration])

  const portalTarget = document.getElementById(portalHostId)
  if (!portalTarget) return null

  return createPortal(
    <div
      className={`${styles.toast} ${styles[`toast_${variant}`]}`}
      role="alert"
      aria-live="assertive"
    >
      <span className={styles.message}>{message}</span>
      <button
        className={styles.closeBtn}
        type="button"
        onClick={onClose}
        aria-label="Zamknij"
      >
        <Close width="0.875em" height="0.875em" />
      </button>
    </div>,
    portalTarget,
  )
}
